import React from 'react';
import { observer } from 'mobx-react-lite';
import { cssVar, Flex, Box, Text } from '@cad-arc';
import { UnitQueueWireDto, ColumnDefinitionEnum } from '@mark43/cad-api';
import { WorkspaceQueueColumn } from '../../panels/workspace-queue-column';
import columnDefinitionEnum from '../../queues/column-definition.enum';
import { BaseCellStyledBox } from '../../common/components/fr-queues/base-cell-styled-box';
import { FrConfiguredPriorityBox } from '../../common/components/fr-queues/fr-configured-priority-box';
import { isValidLatLng } from '../../location/helpers';
import { EsriMapEntityTypeEnum } from '../../esri/types';
import { useOpenFrEventDetails } from '../../fr-event-details-page/hooks/use-open-fr-event-details';
import { useViewLocationOnMap } from '../../location/hooks/use-view-location-on-map';
import { FR_EVENT_DETAILS_TABS } from '../../fr-event-details-page/constants';
import { FrMinutesCell, FrTimeCell } from '../../common/components/fr-queues/fr-time-cells';
import { colorSoundConfigStore } from '../../configurable-colors-sounds/color-sound-config-store';
import { useUnitStateColor } from '../../configurable-colors-sounds/use-configured-colors';
import { getConfiguredUnitStatusColors } from '../../configurable-colors-sounds/unit-status-colors';
import { FrTooltipCell } from '../../common/components/fr-queues/fr-tooltip-cell';
import { DataPrivacy } from '../../common/components/datadog-privacy';
import { UnitsDisplay } from '../../common/components/units-display';
import { ASSIGNED_UNITS_CELL_WIDTH } from '../constants';

const UnitStatusColorCodedValue = observer(function UnitStatusColorCodedValue({
    data,
    value,
}: {
    data: UnitQueueWireDto;
    value: React.ReactNode;
}) {
    const configuredColor = useUnitStateColor(data.unitStateId);
    const unitStatus = getConfiguredUnitStatusColors(configuredColor, data.unitStateGroup);
    return (
        <Flex
            h="100%"
            w="100%"
            bgColor={
                configuredColor.matched || data.unitStateGroup
                    ? unitStatus.fill
                    : unitStatus.text
            }
            overflow="hidden"
            alignItems="center"
            justifyContent="center"
            textTransform="uppercase"
            borderRadius="base"
        >
            <Text style={{color: unitStatus.text}}>{value}</Text>
        </Flex>
    );
});

const ColorCodedCell = ({ data, value }: { data: UnitQueueWireDto; value: React.ReactNode }) => (
    <DataPrivacy privacy="mask">
        <BaseCellStyledBox $fontSize={cssVar('arc.fontSizes.sm')} $padding={cssVar('arc.space.half')}>
            <UnitStatusColorCodedValue data={data} value={value} />
        </BaseCellStyledBox>
    </DataPrivacy>
);

// Before configurable colors, this column was plain text in FR.
const StatusColorCodedCell = observer(function StatusColorCodedCell({
    data,
    value,
}: {
    data: UnitQueueWireDto;
    value: string | undefined;
}) {
    if (!colorSoundConfigStore.isEnabled) {
        return <DataPrivacy privacy="mask"><FrTooltipCell text={value} /></DataPrivacy>;
    }
    return <ColorCodedCell data={data} value={value} />;
});

export const FrUnitsCompactQueueCell = ({
    column,
    data,
}: {
    column: WorkspaceQueueColumn;
    data: UnitQueueWireDto;
}) => {
    const item = columnDefinitionEnum[column.columnDefinition];
    const openFrEventDetails = useOpenFrEventDetails();
    const viewLocationOnMap = useViewLocationOnMap();

    const openEventNarrativeTab = () => {
        if (!data?.eventQueueView?.id) return;
        openFrEventDetails(data.eventQueueView.id, FR_EVENT_DETAILS_TABS.NARRATIVE);
    };

    const handleViewUnitOnMap = () => {
        viewLocationOnMap({
            entityId: data.id,
            entityType: EsriMapEntityTypeEnum.UNIT,
            shouldMoveMapCenterToLoggedInUnit: false,
        });
    };

    const handleViewEventOnMap = () => {
        const isValidEventLocation = isValidLatLng(
            data.eventQueueView?.latitude,
            data.eventQueueView?.longitude
        );
        if (!isValidEventLocation || !data.eventQueueView?.id) return;
        viewLocationOnMap({
            entityId: data.eventQueueView.id,
            entityType: EsriMapEntityTypeEnum.EVENT,
            latitude: data.eventQueueView.latitude,
            longitude: data.eventQueueView.longitude,
            shouldMoveMapCenterToLoggedInUnit: false,
        });
    };

    switch (item.name) {
        case ColumnDefinitionEnum.UNIT_LOCATION.name:
            return (
                <DataPrivacy privacy="mask">
                    <Box
                        onClick={handleViewUnitOnMap}
                        _hover={{
                            cursor: 'pointer',
                            backgroundColor: 'var(--arc-colors-interactive-hover)',
                        }}
                    >
                        <FrTooltipCell text={item.getFlatValue(data)} />
                    </Box>
                </DataPrivacy>
            );
        case ColumnDefinitionEnum.EVENT_LOCATION.name:
            return (
                <DataPrivacy privacy="mask">
                    <Box
                        onClick={handleViewEventOnMap}
                        _hover={{
                            cursor: 'pointer',
                            backgroundColor: 'var(--arc-colors-interactive-hover)',
                        }}
                    >
                        <FrTooltipCell text={item.getFlatValue(data)} />
                    </Box>
                </DataPrivacy>
            );
        case ColumnDefinitionEnum.PRIORITY.name:
            if (data.eventQueueView?.priority) {
                return (
                    <DataPrivacy privacy="mask">
                        <BaseCellStyledBox
                            $fontSize={cssVar('arc.fontSizes.sm')}
                            $padding={cssVar('arc.space.half')}
                            borderRadius={cssVar('arc.radii.base')}
                        >
                            <FrConfiguredPriorityBox eventQueueView={data.eventQueueView}>
                                {item.getFlatValue(data)}
                            </FrConfiguredPriorityBox>
                        </BaseCellStyledBox>
                    </DataPrivacy>
                );
            }
            break;
        case ColumnDefinitionEnum.EVENT_NUMBER_LAST_4_DIGITS.name:
        case ColumnDefinitionEnum.EVENT_NUMBER.name:
            if (data.eventQueueView?.id) {
                return (
                    <DataPrivacy privacy="mask">
                        <Box
                            onClick={openEventNarrativeTab}
                            _hover={{
                                cursor: 'pointer',
                                backgroundColor: 'var(--arc-colors-interactive-hover)',
                            }}
                        >
                            <BaseCellStyledBox>{item.getFlatValue(data)}</BaseCellStyledBox>
                        </Box>
                    </DataPrivacy>
                );
            }
            break;
        case ColumnDefinitionEnum.TIME_ARRIVED.name:
            return <DataPrivacy privacy="mask"><FrTimeCell dateStr={data.eventQueueView?.timeArrived} /></DataPrivacy>;
        case ColumnDefinitionEnum.TIME_RECEIVED.name:
            return <DataPrivacy privacy="mask"><FrTimeCell dateStr={data.eventQueueView?.timeReceived} /></DataPrivacy>;
        case ColumnDefinitionEnum.TIME_DISPATCHED.name:
        case ColumnDefinitionEnum.MINUTES_STANDING.name:
            return <DataPrivacy privacy="mask"><FrMinutesCell dateStr={data.eventQueueView?.timeDispatched} /></DataPrivacy>;
        case ColumnDefinitionEnum.MINUTES_WORKING.name:
        case ColumnDefinitionEnum.TIME_TRANSMITTED.name:
            return <DataPrivacy privacy="mask"><FrMinutesCell dateStr={data.eventQueueView?.timeTransmitted} /></DataPrivacy>;
        case ColumnDefinitionEnum.MINUTES_IN_STATUS.name:
            return <DataPrivacy privacy="mask"><FrMinutesCell dateStr={data.timeInStatusDateTime} /></DataPrivacy>;
        case ColumnDefinitionEnum.TIME_SINCE_UNIT_LOCATION_UPDATED.name:
            return <DataPrivacy privacy="mask"><FrMinutesCell dateStr={data.unitLocationInfoCreatedDateTime} /></DataPrivacy>;
        case ColumnDefinitionEnum.CALL_SIGN_COLOR_CODED.name:
        case ColumnDefinitionEnum.STATUS_CODE_COLOR_CODED.name:
            return <ColorCodedCell data={data} value={item.getFlatValue(data)} />;
        case ColumnDefinitionEnum.STATUS_COLOR_CODED.name:
            return <StatusColorCodedCell data={data} value={item.getFlatValue(data)} />;
        case ColumnDefinitionEnum.ASSIGNED_UNITS.name:
            return (
                <DataPrivacy privacy="mask">
                    <BaseCellStyledBox>
                        <UnitsDisplay
                            width={ASSIGNED_UNITS_CELL_WIDTH}
                            units={data.eventQueueView?.assignedUnits ?? []}
                        />
                    </BaseCellStyledBox>
                </DataPrivacy>
            );
        default:
            return <DataPrivacy privacy="mask"><FrTooltipCell text={item.getFlatValue(data)} /></DataPrivacy>;
    }

    return <DataPrivacy privacy="mask"><FrTooltipCell text={item.getFlatValue(data)} /></DataPrivacy>;
};
